import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import BackToHome from '../../components/feature/BackToHome';
import BHSModelDiagram from '../../components/feature/BHSModelDiagram';
import RevealStagger from '../../components/feature/RevealStagger';
import SectionHeader from '../../components/feature/SectionHeader';
import { fetchApproach, type Approach } from '../../services/approach';
import { fetchBhsModell, type BhsModell } from '../../services/bhsModell';
import Seo, { buildBreadcrumbJsonLd, SITE_URL } from '../../components/feature/Seo';

const PILLARS_FALLBACK = [
  {
    short: 'B',
    title: 'Business',
    subtitle: 'Strukturen, Ziele & Umsetzung',
    description:
      'Die rationale Ebene. Klare Ziele, belastbare Strukturen und Entscheidungen, die im Markt halten. 20+ Jahre Erfahrung als Führungskraft und Berater - vom Konzern bis zum Mittelstand.',
    icon: 'ri-building-4-line',
    accent: '#1A365D',
    points: [
      'Strategische Ausrichtung & Zielklarheit',
      'Entscheidungs- & Umsetzungsstärke',
      'Strukturen, Prozesse & Rollen',
      'Geschäftliche Wirksamkeit',
    ],
  },
  {
    short: 'H',
    title: 'Human',
    subtitle: 'Kommunikation & Potentiale',
    description:
      'Die menschliche Ebene. Beziehung, Haltung und innere Dynamik. Wo blockieren Muster den Fortschritt? Welche Potentiale liegen brach? Hier setzen wir an - im Team und in dir selbst.',
    icon: 'ri-team-line',
    accent: '#8B1538',
    points: [
      'Klare, ehrliche Kommunikation',
      'Potentiale & Stärken aktivieren',
      'Innere & Team-Dynamiken klären',
      'Präsenz & Beziehungsfähigkeit',
    ],
  },
  {
    short: 'S',
    title: 'Spirit',
    subtitle: 'Bewusstsein & Transformation',
    description:
      'Die tiefste Ebene. Bewusstsein, Muster und Resonanzfelder, die Verhalten erst formen. Hier lösen sich blockierende Programmierungen - und Potential entfaltet sich.',
    icon: 'ri-sparkling-2-line',
    accent: '#C9A961',
    points: [
      'Bewusstseinsarbeit & Selbstwahrnehmung',
      'Tiefe Muster & Glaubenssätze auflösen',
      'Energetische Klarheit & Resonanz',
      'Potentialentfaltung & Sinn',
    ],
  },
];

const BENEFITS_FALLBACK = [
  { text: 'Klare strategische Ausrichtung & belastbare Entscheidungen', icon: 'ri-focus-3-line' },
  { text: 'Spürbar mehr Präsenz, Souveränität & Wirkung', icon: 'ri-user-star-line' },
  { text: 'Auflösung blockierender Muster auf allen Ebenen', icon: 'ri-shield-flash-line' },
  { text: 'Nachhaltige Transformation, die im Alltag trägt', icon: 'ri-plant-line' },
  { text: 'Integration von Business-Logik und innerer Reife', icon: 'ri-infinity-line' },
  { text: 'Führung, die Sinn und Ergebnis verbindet', icon: 'ri-flag-2-line' },
];

const METHODS_FALLBACK = [
  { text: 'Systemisches Coaching', icon: 'ri-mind-map' },
  { text: 'Strategieberatung', icon: 'ri-route-line' },
  { text: 'Leadership Sparring', icon: 'ri-sword-line' },
  { text: 'Tiefenpsychologische Begleitung', icon: 'ri-brain-line' },
  { text: 'Energiearbeit', icon: 'ri-sparkling-line' },
  { text: 'Bewusstseinstraining', icon: 'ri-eye-line' },
  { text: 'Resonanzfeld-Arbeit', icon: 'ri-rhythm-line' },
  { text: 'Achtsamkeit & Präsenz', icon: 'ri-leaf-line' },
  { text: 'Klärungs- & Aufstellungsformate', icon: 'ri-group-line' },
];

export default function BhsModellPage() {
  const [approach, setApproach] = useState<Approach | null>(null);
  const [bhsModell, setBhsModell] = useState<BhsModell | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [approachData, bhsModellData] = await Promise.all([
          fetchApproach(),
          fetchBhsModell(),
        ]);
        setApproach(approachData);
        setBhsModell(bhsModellData);
      } catch (error) {
        console.error('Failed to load BHS model data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const seoTitle = 'BHS-Modell - Business, Human, Spirit | Gerald Fasching';
  const seoDescription =
    'Das BHS-Modell von Gerald Fasching verbindet Business (Strukturen, Strategie, Umsetzung), Human (Kommunikation, Potentiale, Beziehung) und Spirit (Bewusstsein, Muster, Transformation) zu einem integralen Arbeitsrahmen für wirksame Führung und persönliche Transformation.';
  const bhsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    '@id': `${SITE_URL}/bhs-modell#concept`,
    name: 'BHS-Modell',
    alternateName: 'Business, Human, Spirit',
    creator: { '@id': `${SITE_URL}/#person` },
    about: 'Integrales Coaching- und Führungsmodell verbindet Business, Human und Spirit.',
    description: seoDescription,
    inLanguage: 'de-AT',
  };
  const seo = (
    <Seo
      title={seoTitle}
      description={seoDescription}
      path="/bhs-modell"
      jsonLd={[
        bhsJsonLd,
        buildBreadcrumbJsonLd([
          { name: 'Start', path: '/' },
          { name: 'BHS-Modell', path: '/bhs-modell' },
        ]),
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <LoadingSpinner bgColor="bg-[#FAF8F5]" />
      </>
    );
  }

  const badge = bhsModell?.badgeText || approach?.badgeText || 'BHS-Modell';
  const heading =
    bhsModell?.heading || approach?.heading || 'Geerdete Weisheit trifft auf strategische Klarheit';
  const features = approach?.features?.length
    ? approach.features
    : [
        { text: 'Evidenzbasierte Strategien', icon: 'ri-bar-chart-line', iconColor: '#C9A961' },
        { text: 'Energetisches Bewusstsein', icon: 'ri-pulse-line', iconColor: '#8B1538' },
        { text: 'Nachhaltige Transformation & Integration', icon: 'ri-plant-line', iconColor: '#1A2B3C' },
      ];

  // Data with fallbacks
  const pillars = bhsModell?.pillars?.length
    ? bhsModell.pillars.map((p) => ({
        short: p.kuerzel,
        title: p.titel,
        subtitle: p.untertitel,
        description: p.beschreibung,
        icon: p.icon,
        accent: p.akzentFarbe,
        points: p.punkte.map((pt) => pt.text),
      }))
    : PILLARS_FALLBACK;

  const benefits = bhsModell?.benefits?.length ? bhsModell.benefits : BENEFITS_FALLBACK;
  const methods = bhsModell?.methods?.length ? bhsModell.methods : METHODS_FALLBACK;

  const syntheseTags = bhsModell?.syntheseTags?.length
    ? bhsModell.syntheseTags.map((t) => t.text)
    : ['klar', 'menschlich', 'bewusst'];

  return (
    <div className="min-h-screen bg-[#FAF8F5] overflow-x-hidden">
      {seo}
      {/* HERO ============================================================ */}
      <section id="bhs-uebersicht" className="relative overflow-hidden bg-[#FAF8F5] px-6 pb-16 pt-32 lg:px-12 lg:pb-24 lg:pt-40">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, #1A2B3C 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div
          className="pointer-events-none absolute -left-32 -top-32 hidden h-[28rem] w-[28rem] rounded-full bg-[#8B1538]/[0.04] blur-3xl lg:block"
        />
        <div
          className="pointer-events-none absolute -right-40 top-40 hidden h-[32rem] w-[32rem] rounded-full bg-[#C9A961]/[0.08] blur-3xl lg:block"
        />

        <div className="relative mx-auto max-w-6xl">
          <BackToHome />


          <RevealStagger animateOnMount className="mx-auto max-w-3xl text-center">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#8B1538]/20 bg-white/70 px-4 py-2 backdrop-blur-sm">
              <i className="ri-compass-3-line flex h-4 w-4 items-center justify-center text-[#8B1538]"></i>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8B1538] lg:text-[13px]">
                {badge}
              </span>
            </div>

              <h1
                className="mb-6 font-serif text-[1.75rem] font-bold leading-snug tracking-[-0.005em] text-[#1A2B3C] md:text-[2.125rem] lg:text-[2.75rem]"
              style={{ fontFamily: 'Lora, serif' }}
            >
              {heading}
            </h1>

            <p className="mx-auto mb-10 max-w-2xl text-base leading-relaxed text-[#1A2B3C]/75 lg:text-lg">
              {bhsModell?.heroDescription ? (
                bhsModell.heroDescription
              ) : (
                <>
                  Das BHS-Modell ist der von Gerald Fasching entwickelte Rahmen für moderne
                  Transformation. Es verbindet, was zu oft getrennt gehalten wird:{' '}
                  <span className="font-semibold text-[#1A2B3C]">strategisches Denken</span> und{' '}
                  <span className="font-semibold text-[#1A2B3C]">innere Klarheit</span> - für echte
                  Wirksamkeit in Führung und Leben.
                </>
              )}
            </p>

            <div className="flex justify-center">
              <Link
                to={bhsModell?.primaryButtonLink ?? '/#coaching-pakete'}
                className="group inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#8B1538] px-7 py-3.5 text-sm font-semibold leading-none text-white shadow-lg shadow-[#8B1538]/25 transition-all hover:bg-[#6B0F2A] hover:shadow-xl active:translate-y-[1px]"
              >
                {bhsModell?.primaryButtonText ?? 'Coaching-Pakete ansehen'}
                <i className="ri-arrow-right-line ml-2 flex h-4 w-4 items-center justify-center transition-transform group-hover:translate-x-1"></i>
              </Link>
            </div>

            {/* Scroll hint to flagship diagram below */}
            <a
              href="#framework"
              aria-label="Zum BHS-Modell scrollen"
              className="group mx-auto mt-14 flex h-10 w-10 items-center justify-center rounded-full border border-[#1A365D]/15 text-[#1A365D]/55 transition-all hover:border-[#8B1538]/40 hover:text-[#8B1538]"
            >
              <i className="ri-arrow-down-line flex h-4 w-4 items-center justify-center text-base transition-transform group-hover:translate-y-0.5"></i>
            </a>
          </RevealStagger>
        </div>
      </section>

      {/* FLAGSHIP DIAGRAM SECTION ========================================= */}
      <section id="framework" className="relative overflow-hidden bg-[#FAF6EE] px-6 py-16 lg:px-12 lg:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, #1A2B3C 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
        <div className="relative mx-auto max-w-5xl">
          <BHSModelDiagram
            eyebrow="Das Diagramm"
            title="Das BHS-Modell"
            caption="Die Integration von Business, Mensch und Bewusstsein für nachhaltige Transformation."
            background="transparent"
          />
        </div>
      </section>

      {/* THREE PILLARS ==================================================== */}
      <section
        id="saeulen"
        className="relative overflow-hidden bg-[#1A365D] px-6 py-16 lg:px-12 lg:py-28"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, #C9A961 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative mx-auto max-w-7xl">
          <RevealStagger className="mx-auto mb-12 max-w-3xl lg:mb-20">
            <SectionHeader
              variant="dark"
              badgeIcon="ri-stack-line"
              badgeLabel={bhsModell?.pillarsBadge ?? 'Drei Säulen'}
              heading={bhsModell?.pillarsHeading ?? 'Business · Human · Spirit'}
              subtitle={
                bhsModell?.pillarsSubtitle ??
                'Strategie ohne Mensch bleibt blutleer. Mensch ohne Bewusstsein bleibt im Muster. Bewusstsein ohne Struktur bleibt folgenlos. Erst die Integration aller drei Ebenen macht Führung wirksam und Veränderung nachhaltig.'
              }
            />
          </RevealStagger>

          <RevealStagger className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            {pillars.map((p, idx) => (
              <article
                key={p.short}
                className={`group relative flex flex-col overflow-hidden rounded-2xl bg-white/[0.04] p-8 ring-1 ring-white/10 backdrop-blur-sm transition-all duration-500 hover:bg-white/[0.06] hover:ring-white/20 lg:p-10 ${idx === 1 ? 'lg:-translate-y-4' : ''}`}
              >
                {/* Accent bar */}
                <div
                  className="absolute left-0 top-0 h-full w-1"
                  style={{ background: `linear-gradient(to bottom, ${p.accent}, transparent)` }}
                />

                <div className="mb-6 flex items-center gap-5">
                  <div
                    className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl shadow-lg lg:h-20 lg:w-20"
                    style={{ backgroundColor: p.accent }}
                  >
                    <span
                      className="font-serif text-3xl font-bold text-white lg:text-4xl"
                      style={{ fontFamily: 'Lora, serif' }}
                    >
                      {p.short}
                    </span>
                  </div>
                  <div>
                    <h3
                      className="font-serif text-3xl font-bold text-white lg:text-4xl"
                      style={{ fontFamily: 'Lora, serif' }}
                    >
                      {p.title}
                    </h3>
                    <p className="text-xs font-medium uppercase tracking-widest text-[#C9A961] lg:text-[13px]">
                      {p.subtitle}
                    </p>
                  </div>
                </div>

                <p className="mb-7 text-sm leading-relaxed text-white/75 lg:text-base">
                  {p.description}
                </p>

                <ul className="mt-auto space-y-3 border-t border-white/10 pt-6">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-sm leading-relaxed text-white/85 lg:text-base">
                      <i
                        className="ri-checkbox-blank-circle-fill mt-2 flex h-1.5 w-1.5 flex-shrink-0 items-center justify-center"
                        style={{ color: p.accent === '#1A365D' ? '#C9A961' : p.accent }}
                      ></i>
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>

                <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-20 blur-2xl transition-opacity group-hover:opacity-30" style={{ backgroundColor: p.accent }} />
              </article>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* INTEGRATION ===================================================== */}
      <section id="synthese" className="relative overflow-hidden bg-[#FAF8F5] px-6 py-16 lg:px-12 lg:py-28">
        <RevealStagger className="relative mx-auto max-w-5xl text-center">
          <SectionHeader
            badgeIcon="ri-infinity-line"
            badgeLabel={bhsModell?.syntheseBadge ?? 'Integration'}
            heading={bhsModell?.syntheseHeading ?? 'Wo Strategie, Mensch und Bewusstsein zusammenkommen'}
            className="mb-8"
          />

          {/* Venn diagram visual — square container so circles overlap consistently across breakpoints */}
          <div className="relative mx-auto mb-10 h-72 w-72 sm:h-96 sm:w-96 lg:mb-14 lg:h-[28rem] lg:w-[28rem]">
            <div
              className="absolute left-1/2 top-0 h-48 w-48 -translate-x-1/2 rounded-full sm:h-64 sm:w-64 lg:h-72 lg:w-72"
              style={{ backgroundColor: '#1A365D', mixBlendMode: 'multiply', opacity: 0.72 }}
            />
            <div
              className="absolute bottom-0 left-0 h-48 w-48 rounded-full sm:h-64 sm:w-64 lg:h-72 lg:w-72"
              style={{ backgroundColor: '#8B1538', mixBlendMode: 'multiply', opacity: 0.72 }}
            />
            <div
              className="absolute bottom-0 right-0 h-48 w-48 rounded-full sm:h-64 sm:w-64 lg:h-72 lg:w-72"
              style={{ backgroundColor: '#C9A961', mixBlendMode: 'multiply', opacity: 0.78 }}
            />

            {/* Labels in solo (non-overlapping) regions */}
            <span
              className="absolute left-1/2 top-4 z-10 -translate-x-1/2 font-serif text-xl font-bold text-white drop-shadow-md sm:top-6 sm:text-2xl lg:top-7 lg:text-3xl"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Business
            </span>
            <span
              className="absolute bottom-5 left-4 z-10 font-serif text-xl font-bold text-white drop-shadow-md sm:bottom-7 sm:left-7 sm:text-2xl lg:bottom-12 lg:left-10 lg:text-3xl"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Human
            </span>
            <span
              className="absolute bottom-5 right-4 z-10 font-serif text-xl font-bold text-white drop-shadow-md sm:bottom-7 sm:right-7 sm:text-2xl lg:bottom-12 lg:right-10 lg:text-3xl"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Spirit
            </span>

            {/* Center label */}
            <div className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
              <div className="rounded-full bg-white px-5 py-2 shadow-xl ring-1 ring-[#1A2B3C]/10">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#8B1538] lg:text-[13px]">
                  Wirksamkeit
                </span>
              </div>
            </div>
          </div>

          <p className="mx-auto mb-8 max-w-3xl text-base leading-relaxed text-[#1A2B3C]/80 lg:text-lg">
            {bhsModell?.syntheseDescription ??
              'Die meisten Ansätze bedienen entweder den Kopf oder das Herz. Entweder Strategie oder Spiritualität. Das BHS-Modell verbindet beides zu einem kohärenten Arbeitsrahmen. So entsteht Führung, die strategisch klar, menschlich nahbar und bewusst getragen ist.'}
          </p>

          <div className="inline-flex flex-wrap items-center justify-center gap-3 rounded-full border border-[#1A2B3C]/10 bg-white px-6 py-3 shadow-sm">
            {syntheseTags.flatMap((tag, idx) =>
              idx > 0
                ? [
                    <span key={`dot-${idx}`} className="text-[#C9A961]">·</span>,
                    <span key={tag} className="text-sm font-semibold text-[#1A2B3C]">{tag}</span>,
                  ]
                : [<span key={tag} className="text-sm font-semibold text-[#1A2B3C]">{tag}</span>]
            )}
          </div>
        </RevealStagger>
      </section>

      {/* BESONDERE MERKMALE ============================================= */}
      <section id="auszeichnung" className="bg-white px-6 py-16 lg:px-12 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <RevealStagger className="mx-auto mb-10 max-w-3xl lg:mb-16">
            <SectionHeader
              badgeIcon="ri-award-line"
              badgeLabel="Besondere Merkmale"
              heading="Was den BHS-Ansatz auszeichnet"
            />
          </RevealStagger>

          <RevealStagger className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl border border-[#1A2B3C]/[0.06] bg-[#FAF8F5] p-8 transition-all hover:-translate-y-1 hover:shadow-xl lg:p-10"
              >
                <div
                  className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${feature.iconColor || '#C9A961'}1a` }}
                >
                  <i
                    className={`${feature.icon || 'ri-checkbox-circle-line'} text-2xl`}
                    style={{ color: feature.iconColor || '#C9A961' }}
                  ></i>
                </div>
                <h3
                  className="mb-3 font-serif text-lg font-bold leading-snug text-[#1A2B3C] md:text-xl lg:text-[1.375rem]"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  {feature.text}
                </h3>
                <div
                  className="h-0.5 w-10 rounded-full transition-all duration-300 group-hover:w-20"
                  style={{ backgroundColor: feature.iconColor || '#C9A961' }}
                />
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* IHR NUTZEN ====================================================== */}
      <section id="nutzen" className="relative overflow-hidden bg-[#F5F1EB] px-6 py-16 lg:px-12 lg:py-28">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#8B1538]/[0.04] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[#C9A961]/[0.08] blur-3xl" />

        <div className="relative mx-auto max-w-6xl">
          <RevealStagger className="grid grid-cols-1 gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:gap-20">
            <div className="lg:sticky lg:top-32">
              <SectionHeader
                align="left"
                badgeIcon="ri-shield-flash-line"
                badgeLabel={bhsModell?.nutzenBadge ?? 'Ihr Nutzen'}
                heading={bhsModell?.nutzenHeading ?? 'Mehr Klarheit, Präsenz und Wirksamkeit'}
                subtitle={
                  bhsModell?.nutzenSubtitle ??
                  'Die Arbeit mit dem BHS-Modell wirkt auf allen Ebenen - innen wie außen. Was sich verändert, zeigt sich in besseren Entscheidungen, klareren Beziehungen und einer Präsenz, die Räume öffnet.'
                }
              />
            </div>

            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {benefits.map((b, idx) => (
                <li
                  key={idx}
                  className="group flex items-start gap-4 rounded-2xl border border-[#1A2B3C]/[0.06] bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md lg:p-6"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#8B1538]/10 transition-colors group-hover:bg-[#8B1538]/15">
                    <i className={`${b.icon} text-lg text-[#8B1538]`}></i>
                  </div>
                  <span className="pt-1.5 text-sm leading-snug text-[#1A2B3C] lg:text-[15px]">
                    {b.text}
                  </span>
                </li>
              ))}
            </ul>
          </RevealStagger>
        </div>
      </section>

      {/* METHODEN ======================================================== */}
      <section id="methoden" className="bg-white px-6 py-16 lg:px-12 lg:py-28">
        <div className="mx-auto max-w-6xl">
          <RevealStagger className="mx-auto mb-10 max-w-3xl lg:mb-14">
            <SectionHeader
              badgeIcon="ri-tools-line"
              badgeLabel={bhsModell?.methodenBadge ?? 'Methoden & Werkzeuge'}
              heading={bhsModell?.methodenHeading ?? 'Fundiert. Vielschichtig. Passgenau.'}
              subtitle={
                bhsModell?.methodenSubtitle ??
                'Im BHS-Modell verbinde ich klassische Beratung, systemische Begleitung und tiefe Bewusstseinsarbeit - immer abgestimmt auf Person und Anliegen.'
              }
            />
          </RevealStagger>

          <RevealStagger className="flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4">
            {methods.map((m, idx) => (
              <div
                key={idx}
                className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#1A2B3C]/10 bg-[#FAF8F5] px-4 py-2.5 text-xs transition-all hover:-translate-y-0.5 hover:border-[#8B1538]/30 hover:bg-white hover:shadow-md sm:gap-2.5 sm:px-5 sm:py-3 sm:text-sm"
              >
                <i className={`${m.icon} text-[#C9A961] transition-colors group-hover:text-[#8B1538]`}></i>
                <span className="font-medium text-[#1A2B3C]">{m.text}</span>
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CLOSING QUOTE + CTA ============================================ */}
      <section id="bhs-termin" className="relative overflow-hidden bg-[#FAF6EE] px-6 py-20 lg:px-12 lg:py-28">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, #1A2B3C 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
        <div className="pointer-events-none absolute -left-32 top-1/2 hidden h-96 w-96 -translate-y-1/2 rounded-full bg-[#8B1538]/[0.05] blur-3xl lg:block" />
        <div className="pointer-events-none absolute -right-32 top-1/2 hidden h-96 w-96 -translate-y-1/2 rounded-full bg-[#C9A961]/[0.12] blur-3xl lg:block" />

        <div className="relative mx-auto max-w-4xl">
          <RevealStagger className="flex flex-col items-center rounded-3xl border border-[#1A365D]/10 bg-white/70 px-6 py-12 text-center shadow-sm backdrop-blur-sm sm:px-12 lg:px-16 lg:py-16">
            <i className="ri-double-quotes-l mb-5 inline-flex h-12 w-12 items-center justify-center text-5xl text-[#C9A961]"></i>
            <p
              className="mb-10 font-serif text-2xl font-medium leading-relaxed text-[#1A365D] md:text-3xl lg:text-[2.25rem] lg:leading-[1.3]"
              style={{ fontFamily: 'Lora, serif' }}
            >
              {bhsModell?.closingQuote
                ? `"${bhsModell.closingQuote}"`
                : <>
                    "Integration statt Entweder-oder. Klar, menschlich und bewusst -<br className="hidden lg:block" />
                    für Führung, die wirksam bleibt."
                  </>}
            </p>
            <div className="mb-10 flex items-center justify-center gap-3">
              <span className="h-px w-8 bg-[#C9A961]" />
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8B1538] lg:text-[13px]">
                {bhsModell?.closingAuthor ?? 'Gerald Fasching'}
              </p>
              <span className="h-px w-8 bg-[#C9A961]" />
            </div>

            <div className="flex w-full max-w-xl flex-col gap-3 sm:flex-row sm:justify-center" data-reveal-expand="">
              <Link
                to={bhsModell?.closingPrimaryButtonLink ?? '/#coaching-pakete'}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#8B1538] px-7 py-3.5 text-sm font-semibold leading-none text-white shadow-lg shadow-[#8B1538]/25 transition-all hover:bg-[#6B0F2A] hover:shadow-xl active:translate-y-[1px]"
              >
                {bhsModell?.closingPrimaryButtonText ?? 'Coaching-Pakete ansehen'}
                <i className="ri-arrow-right-line ml-2 flex h-4 w-4 items-center justify-center"></i>
              </Link>
              <Link
                to={bhsModell?.closingSecondaryButtonLink ?? '/kontakt'}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-[#1A2B3C]/25 bg-white px-7 py-3.5 text-sm font-semibold leading-none text-[#1A2B3C] transition-all hover:border-[#1A2B3C] hover:bg-[#1A2B3C] hover:text-white active:translate-y-[1px]"
              >
                {bhsModell?.closingSecondaryButtonText ?? 'Kontakt aufnehmen'}
              </Link>
            </div>
          </RevealStagger>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
